import {Context, createContext} from "@lit/context";
import {
    CellProxy,
    ZomeProxy,
    ZomeProxyConstructor,
    CellMixin,
    SignalLog,
    prettySignalLogs, HCL, MyDictionary, EntryDef, DnaInfo, AgentId, ZomeInfo,
} from "@ddd-qc/cell-proxy";
import {ViewModel} from "./ViewModel";
import {SignalCb, ZomeName, Signal, SignalType} from "@holochain/client";
import {AppSignal} from "@holochain/client/lib/api/app/types";
import {DnaViewModel} from "./DnaViewModel";

export type ZvmConstructor = {new(proxy: CellProxy, dvmParent: DnaViewModel, isMainView: boolean, zomeName?: ZomeName): ZomeViewModel} /*& typeof ZomeSpecific;*/

/** (EXPERIMENTAL) Class Decorator */
export function zvm(zProxyCtor: typeof ZomeProxy) {
    return (ctor: Function) => {
        //let zvmCtor = (ctor as typeof ZomeViewModel);
        (ctor as any).ZOME_PROXY = zProxyCtor;
        //get zomeProxy(): DummyZomeProxy {return this._zomeProxy as DummyZomeProxy;}
        //(ctor as any).zomeProxy = function() {return (ctor as any)._zomeProxy as typeof zProxyFactory;}
        //(ctor as any).zomeProxy = (ctor as any)._zomeProxy as typeof zProxyFactory;
    }
}


/**
 * Abstract ViewModel for a Zome.
 * It extends a ViewModel by adding a ZomeProxy.
 * Views are required to use this in order to interact with the ZomeProxy.
 * The perspective is the data from the Zome that is transformed and enhanced in order to be consumed by a View.
 * It can be automatically updated by Signals or the Zome Scheduler.
 */
export abstract class ZomeViewModel extends CellMixin(ViewModel) {

    /** Zome proxy constructor */
    static ZOME_PROXY: ZomeProxyConstructor;
    protected _zomeProxy: ZomeProxy;
    /* Child class should implement with child proxy class as return type */
    abstract get zomeProxy(): ZomeProxy;
    getProxyConstructor(): ZomeProxyConstructor {
        return (this.constructor as typeof ZomeViewModel).ZOME_PROXY;
    }


    signalHandler?: SignalCb;

    /** Zome name */
    static get DEFAULT_ZOME_NAME(): string {
        return this.ZOME_PROXY.DEFAULT_ZOME_NAME;
    }
    zomeName!: ZomeName;

    static get ENTRY_TYPES(): string[] {
        return this.ZOME_PROXY.ENTRY_TYPES;
    }
    static get LINK_TYPES(): string[] {
        return this.ZOME_PROXY.LINK_TYPES;
    }

    protected _dvmParent: DnaViewModel;


    /** Ctor */
    constructor(cellProxy: CellProxy, dvmParent: DnaViewModel, isMainView: boolean, zomeName?: ZomeName) {
        super(isMainView);
        this._dvmParent = dvmParent;
        const zProxyCtor = this.getProxyConstructor();
        if (!zProxyCtor) {
            throw Error("ZOME_PROXY static field undefined in ZVM subclass " + this.constructor.name);
        }
        if (zomeName) {
            this.zomeName = zomeName;
            this._zomeProxy = new zProxyCtor(cellProxy, this.zomeName);
        } else {
            this._zomeProxy = new zProxyCtor(cellProxy);
            this.zomeName = this._zomeProxy.defaultZomeName;
        }
        this._cell = cellProxy.cell;
        cellProxy.addSignalHandler( (signal: Signal) => this.handleZomeSignal(signal));
    }


    /** Notify DVM parent */
    protected override notifySubscribers(): boolean {
        const hasChanged = super.notifySubscribers();
        if (hasChanged) {
            this._dvmParent.zvmChanged(this);
        }
        return hasChanged;
    }

    /** Filter signal by zome name */
    private handleZomeSignal(signal: Signal) {
        if (SignalType.App != signal.type) {
            return;
        }
        const appSignal: AppSignal = signal.value;
        //console.log("handleZomeSignal()", this.signalHandler, this.zomeName, signal.zome_name)
        if (this.signalHandler && appSignal.zome_name == this.zomeName) {
            this.signalHandler(signal);
        }
    }

    /** */
    getContext(): Context<unknown, unknown> {
        const context = createContext<typeof this>('zvm/'+ this._zomeProxy.defaultZomeName +'/' + this.cell.address.dnaId.b64)
        //console.log({contextType: typeof context})
        return context
    }


    /** */
    dumpSignalLogs(signalLogs: SignalLog[]) {
        console.warn(`All signals from zome "${this.zomeName}"`);
        console.table(prettySignalLogs(signalLogs));
    }


    /** Passthrough public info from DVM */

    get hcl(): HCL { return this._dvmParent.hcl }

    get allEntryDefs(): MyDictionary<MyDictionary<EntryDef>> { return this._dvmParent.allEntryDefs }

    get allZomeInfo(): MyDictionary<ZomeInfo> { return this._dvmParent.allZomeInfo }

    get dnaInfo(): DnaInfo { return this._dvmParent.dnaInfo }

    get livePeers(): AgentId[] { return this._dvmParent.livePeers };

    get zomeNames(): ZomeName[] {return Object.values(this._dvmParent.zomeNames);}
}
